import axios from 'axios';
import { useEffect, useState } from 'react';
import Table from 'react-bootstrap/Table';
import core from '../../core';
import "./News.css";
import { Pagination, Stack } from "@mui/material";
import { useSelector, useDispatch } from 'react-redux';
import { Search, Plus, ThreeDotsVertical } from 'react-bootstrap-icons';
import OutsideClickHandler from 'react-outside-click-handler';
import { Button, Modal } from 'react-bootstrap';
import CreateNews from './CreateNews';
import { notificationActions } from '../../data/notification';
import useViewMode from '../../hooks/useViewMode';
import ViewToggle from '../../components/DataView/ViewToggle';
import DataCards from '../../components/DataView/DataCards';
import useT from '../../i18n/useT';

const priorityClass = { high: "news-badge-high", medium: "news-badge-medium", low: "news-badge-low" };

const News = () => {
    const [filter, setFilter] = useState({ keyword: "", limit: 10, page: 1, asc: "desc", sort_by: "id" });
    const [news, setNews] = useState([]);
    const [paginator, setPaginator] = useState({ from: 0, to: 0, total: 0, pages: 1 });
    const [loading, setLoading] = useState(false);
    const lang = useSelector((state) => state.user.language);
    const role = useSelector((state) => state.user.role);
    const canManage = role === "admin" || role === "moderator";
    const [keyword, setKeyword] = useState("");
    const [newsForm, setNewsForm] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [actionType, setActionType] = useState("add");
    const [formLoader, setFormLoader] = useState(false);
    const [actionsId, setActionsId] = useState("");
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [viewNews, setViewNews] = useState(null);
    const dispatch = useDispatch();
    const { view, setView, isMobile } = useViewMode("news-view");
    const t = useT();
    const priorityLabel = { low: t("news.prLow"), medium: t("news.prMedium"), high: t("news.prHigh") };

    const rowActions = (item) => (
        <div className='table-actions-block'>
            <ThreeDotsVertical height={"19px"} onClick={() => setActionsId(item.id)} style={{ cursor: "pointer" }} />
            {actionsId === item.id && <OutsideClickHandler onOutsideClick={() => setActionsId("")}>
                <div className='table-actions-dropdown'>
                    <div className='table-actions-item' onClick={() => { setActionsId(""); setViewNews(item); }}>{t("common.view")}</div>
                    {canManage && <div className='table-actions-item' onClick={() => { setActionsId(""); setActionType("edit"); setNewsForm({ ...item }); setShowForm(true); }}>{t("common.edit")}</div>}
                    {canManage && <div className='table-actions-item' onClick={() => { setActionsId(""); setNewsForm({ ...item }); setShowConfirmationModal(true); }}>{t("common.delete")}</div>}
                </div>
            </OutsideClickHandler>}
        </div>
    );

    const cardFields = [
        { label: t("news.priority"), value: (item) => <span className={`news-badge ${priorityClass[item.priority] || "news-badge-low"}`}>{priorityLabel[item.priority] || item.priority}</span> },
        { label: t("news.created"), value: (item) => item.create_date ? core.formdatDate(item.create_date, "yyyy-mm-dd") : t("table.noData") },
        { label: t("news.modified"), value: (item) => item.is_modified ? core.formdatDate(item.modify_date, "yyyy-mm-dd") : "—" },
    ];

    const notify = (text, type) => {
        dispatch(notificationActions.changeNotification({ show: true, text, type }));
        setTimeout(() => dispatch(notificationActions.changeNotification({ show: false, text: "", type: "" })), 4000);
    };

    const load = () => {
        setLoading(true);
        axios.get(`/api/news?limit=${filter.limit}&page=${filter.page}&keyword=${filter.keyword}&asc=${filter.asc}&sort_by=${filter.sort_by}`)
            .then((res) => {
                setLoading(false);
                setNews(res.data.data);
                const fromItems = ((filter.page - 1) * filter.limit) + 1;
                const toItems = res.data.total < filter.page * filter.limit ? res.data.total : filter.page * filter.limit;
                const pagesCount = res.data.total % filter.limit === 0 ? res.data.total / filter.limit : parseInt((res.data.total / filter.limit) + 1);
                setPaginator({ from: fromItems, to: toItems, total: res.data.total, pages: pagesCount });
            })
            .catch(() => setLoading(false));
    };

    useEffect(load, [filter]);

    const handlers = {
        submit(e) {
            e.preventDefault();
            setFormLoader(true);
            const req = actionType === "add"
                ? axios.post('/api/news', newsForm)
                : axios.put(`/api/news/${newsForm.id}`, newsForm);
            req.then((res) => {
                setFormLoader(false);
                setShowForm(false);
                notify(res.data.message[lang], "success-notification");
                load();
            }).catch((err) => {
                setFormLoader(false);
                notify(err.response.data.message[lang], "error-notification");
            });
        },
        remove() {
            setFormLoader(true);
            axios.delete(`/api/news/${newsForm.id}`)
                .then((res) => {
                    setFormLoader(false);
                    setShowConfirmationModal(false);
                    notify(res.data.message[lang], "success-notification");
                    load();
                })
                .catch((err) => {
                    setFormLoader(false);
                    notify(err.response.data.message[lang], "error-notification");
                });
        }
    };

    return (
        <div className="page-content-main-container">
            <h2 className="page-title">{t("news.title")}</h2>
            <div className="table-filter-container">
                <div className='data-table-filteres'></div>
                <ViewToggle view={view} setView={setView} isMobile={isMobile} />
                {canManage && <Button variant='primary' onClick={() => { setShowForm(true); setActionType("add"); setNewsForm({}); }} style={{ display: "flex", alignItems: "center", marginRight: "15px", marginLeft: "15px" }}><Plus style={{ fontSize: "20px", fontWeight: "bold" }} /> <span>{t("news.addNews")}</span></Button>}
                <div className='search-by-keyword'>
                    <input placeholder={t("common.search")} type='text' onKeyDown={(e) => { if (e.keyCode === 13) setFilter((prev) => ({ ...prev, keyword })); }} value={keyword} className='form-control' onChange={(e) => setKeyword(e.target.value)} />
                    <button onClick={() => setFilter((prev) => ({ ...prev, keyword }))}><Search /></button>
                </div>
            </div>
            {view === "table" ? (
            <Table responsive>
                <thead>
                    <tr>
                        <th>{t("news.newsTitle")}</th>
                        <th>{t("news.publisher")}</th>
                        <th>{t("news.priority")}</th>
                        <th>{t("news.created")}</th>
                        <th>{t("news.modified")}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {loading && <tr><td colSpan={6}><div style={{ display: "flex", justifyContent: "center" }}><div className="loader" role=""></div></div></td></tr>}
                    {(news.length > 0 && !loading) && news.map((item) => (
                        <tr key={item.id}>
                            <td style={{ cursor: "pointer", fontWeight: 600 }} onClick={() => setViewNews(item)}>{item.title}</td>
                            <td>{item.publisher_name}</td>
                            <td><span className={`news-badge ${priorityClass[item.priority] || "news-badge-low"}`}>{priorityLabel[item.priority] || item.priority}</span></td>
                            <td>{item.create_date ? core.formdatDate(item.create_date, "yyyy-mm-dd") : t("table.noData")}</td>
                            <td>{item.is_modified ? core.formdatDate(item.modify_date, "yyyy-mm-dd") : "—"}</td>
                            <td>
                                {rowActions(item)}
                            </td>
                        </tr>
                    ))}
                    {(!loading && news.length === 0) && <tr><td colSpan={6} style={{ textAlign: "center" }}>{t("table.notFound")}</td></tr>}
                </tbody>
            </Table>
            ) : (
                <DataCards layout={view}
                    items={loading ? [] : news}
                    loading={loading}
                    getKey={(item) => item.id}
                    renderTitle={(item) => item.title}
                    renderSubtitle={(item) => item.publisher_name}
                    fields={cardFields}
                    renderActions={(item) => rowActions(item)}
                    onClick={(item) => setViewNews(item)}
                />
            )}
            <div className="pagination-container">
                <div className="paginatior-left-size">
                    <p className="showing-items-text">{t("table.showing")} {paginator.from} {t("table.of")} {paginator.to}</p>
                    <span style={{ margin: "0 12px" }}>{t("table.total")}: {paginator.total}</span>
                    <span>{t("table.show")}:</span>
                    <select value={filter.limit} onChange={(e) => setFilter((prev) => ({ ...prev, limit: e.target.value }))} className="form-control" style={{ width: "50px" }}>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                    </select>
                </div>
                <Stack spacing={2}>
                    <Pagination count={paginator.pages} page={filter.page} onChange={(value, p) => setFilter((prev) => ({ ...prev, page: p }))} />
                </Stack>
            </div>

            <CreateNews loading={formLoader} formData={newsForm} setFormData={setNewsForm} show={showForm} setShow={setShowForm} submit={handlers.submit} type={actionType} />

            <Modal show={!!viewNews} onHide={() => setViewNews(null)} centered size='lg'>
                <Modal.Header closeButton><h3>{viewNews?.title}</h3></Modal.Header>
                <Modal.Body>
                    <div className='news-view-meta'>
                        <span className={`news-badge ${priorityClass[viewNews?.priority] || "news-badge-low"}`}>{priorityLabel[viewNews?.priority] || viewNews?.priority}</span>
                        <span>{viewNews?.publisher_name}</span>
                        <span>{viewNews?.create_date ? core.formdatDate(viewNews.create_date, "yyyy-mm-dd hr:min") : ""}</span>
                    </div>
                    <p className='news-view-message'>{viewNews?.message}</p>
                </Modal.Body>
            </Modal>

            <Modal show={showConfirmationModal} onHide={() => setShowConfirmationModal(false)}>
                <Modal.Header closeButton><h3>{t("news.removeNews")}</h3></Modal.Header>
                <Modal.Body><p>{t("news.removeConfirm")}</p></Modal.Body>
                <Modal.Footer>
                    <Button type='button' onClick={handlers.remove} variant={formLoader ? 'secondary' : 'primary'} disabled={formLoader}>{formLoader ? <div className="spinner-border spinner-border-sm" role="status"></div> : t("common.yes")}</Button>
                    <Button type='button' onClick={() => setShowConfirmationModal(false)} variant='danger'>{t("common.no")}</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default News;
